/**
 * Shared PDF layout helpers — one pipeline for annual + section exports.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrencyAmount } from "../calculations";
import { summaryMoney, tableMoney } from "./pdfMoney";
import { getPdfFontFamily, registerPdfFonts } from "./pdfFonts";

export { summaryMoney, tableMoney };
export const PDF_MARGIN = 14;
export const PDF_HEADER_H = 12;
export const PDF_FOOTER_H = 12;

/** Fixed vertical rhythm for section headings. */
export const SECTION_MARGIN_ABOVE = 10;
export const SECTION_MARGIN_BELOW = 5;

/** Bar / line charts: cap at ~16:9 instead of stretching full-width flat. */
export const CHART_ASPECT = 16 / 9;
export const CHART_MAX_H_MM = 88;

export type PdfDoc = jsPDF;

export function createReportDoc(): PdfDoc {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  registerPdfFonts(doc);
  return doc;
}

export function pageWidth(doc: PdfDoc): number {
  return doc.internal.pageSize.getWidth();
}

export function pageHeight(doc: PdfDoc): number {
  return doc.internal.pageSize.getHeight();
}

export function contentWidth(doc: PdfDoc): number {
  return pageWidth(doc) - PDF_MARGIN * 2;
}

export interface ReportChrome {
  brand: string;
  periodLabel: string;
  generatedLabel: string;
  pageLabel: (n: number, total: number) => string;
}

/** Draw header/footer on every page after content is done. */
export function applyChrome(doc: PdfDoc, chrome: ReportChrome): void {
  const total = doc.getNumberOfPages();
  const w = pageWidth(doc);
  const h = pageHeight(doc);
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont(getPdfFontFamily(), "normal");
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(chrome.brand, PDF_MARGIN, 8);
    doc.text(chrome.periodLabel, w / 2, 8, { align: "center" });
    doc.text(chrome.generatedLabel, w - PDF_MARGIN, 8, { align: "right" });
    doc.setDrawColor(200);
    doc.line(PDF_MARGIN, 10, w - PDF_MARGIN, 10);
    doc.line(PDF_MARGIN, h - 10, w - PDF_MARGIN, h - 10);
    doc.text(chrome.pageLabel(i, total), w / 2, h - 6, { align: "center" });
  }
}

export function moneyStr(
  n: number,
  currency: string,
  locale: string,
  compact = false
): string {
  return formatCurrencyAmount(n, currency, locale, compact);
}

export function ensureSpace(doc: PdfDoc, y: number, neededMm: number): number {
  const limit = pageHeight(doc) - PDF_MARGIN - PDF_FOOTER_H;
  if (y + neededMm > limit) {
    doc.addPage();
    return PDF_MARGIN + PDF_HEADER_H + 4;
  }
  return y;
}

/**
 * Draw a section title only when the title + first content block fit together
 * (prevents orphaned headings at the bottom of a page).
 */
export function sectionTitle(
  doc: PdfDoc,
  y: number,
  title: string,
  /** Minimum mm of content that must follow on the same page (chart, table start, etc.) */
  keepWithMm = 36
): number {
  y += SECTION_MARGIN_ABOVE;
  y = ensureSpace(doc, y, 8 + SECTION_MARGIN_BELOW + keepWithMm);
  doc.setFont(getPdfFontFamily(), "bold");
  doc.setFontSize(13);
  doc.setTextColor(30);
  doc.text(title, PDF_MARGIN, y);
  doc.setFont(getPdfFontFamily(), "normal");
  return y + SECTION_MARGIN_BELOW + 2;
}

export function bodyText(doc: PdfDoc, y: number, text: string, size = 10): number {
  y = ensureSpace(doc, y, 8);
  doc.setFont(getPdfFontFamily(), "normal");
  doc.setFontSize(size);
  doc.setTextColor(50);
  doc.text(text, PDF_MARGIN, y);
  return y + 5;
}

export function addTable(
  doc: PdfDoc,
  y: number,
  head: string[],
  body: (string | number)[][],
  opts?: { columnStyles?: Record<number, { halign?: "left" | "right" | "center" }> }
): number {
  const startY = ensureSpace(doc, y, 24);
  const columnStyles = opts?.columnStyles;
  autoTable(doc, {
    startY,
    head: [head],
    body,
    margin: {
      left: PDF_MARGIN,
      right: PDF_MARGIN,
      top: PDF_MARGIN + PDF_HEADER_H,
      bottom: PDF_MARGIN + PDF_FOOTER_H,
    },
    styles: {
      font: getPdfFontFamily(),
      fontSize: 8,
      cellPadding: 1.6,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      font: getPdfFontFamily(),
      fontStyle: "bold",
      fillColor: [47, 126, 120],
      textColor: 255,
    },
    alternateRowStyles: { fillColor: [246, 248, 248] },
    columnStyles,
    // jspdf-autotable applies columnStyles to body; force the same halign on headers
    didParseCell: (data) => {
      if (data.section !== "head" || !columnStyles) return;
      const align = columnStyles[data.column.index]?.halign;
      if (align) data.cell.styles.halign = align;
    },
    rowPageBreak: "avoid",
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const finalY = (doc as any).lastAutoTable?.finalY ?? startY + 20;
  return finalY + 6;
}

/**
 * Embed a chart at a capped aspect ratio (default 16:9), centered if narrower than page.
 */
export function addChartImage(
  doc: PdfDoc,
  y: number,
  dataUrl: string | null | undefined,
  aspect = CHART_ASPECT,
  maxH = CHART_MAX_H_MM
): number {
  if (!dataUrl) return y;
  const maxW = contentWidth(doc);
  let w = maxW;
  let h = w / aspect;
  if (h > maxH) {
    h = maxH;
    w = h * aspect;
  }
  y = ensureSpace(doc, y, h + 4);
  const x = PDF_MARGIN + (maxW - w) / 2;
  try {
    doc.addImage(dataUrl, "PNG", x, y, w, h);
    return y + h + 6;
  } catch {
    return y;
  }
}

/** @deprecated Prefer addChartImage for bar/line; keep for square-ish donuts. */
export function addImageSafe(
  doc: PdfDoc,
  y: number,
  dataUrl: string | null | undefined,
  maxH = 70
): number {
  if (!dataUrl) return y;
  // Donut source is 720×480 → 1.5:1
  return addChartImage(doc, y, dataUrl, 720 / 480, maxH);
}

export function downloadPdfBlob(doc: PdfDoc, filename: string): void {
  doc.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
}

export function coverPage(
  doc: PdfDoc,
  opts: {
    brand: string;
    title: string;
    subtitle: string;
    generatedLabel: string;
    tagline: string;
    disclaimer: string;
    viewLabel?: string;
  }
): number {
  const w = pageWidth(doc);
  const h = pageHeight(doc);
  doc.setFillColor(47, 126, 120);
  doc.rect(0, 0, w, 48, "F");
  doc.setTextColor(255);
  doc.setFont(getPdfFontFamily(), "bold");
  doc.setFontSize(28);
  doc.text(opts.brand, PDF_MARGIN, 28);
  doc.setFontSize(12);
  doc.setFont(getPdfFontFamily(), "normal");
  doc.text(opts.tagline, PDF_MARGIN, 38);

  doc.setTextColor(30);
  doc.setFont(getPdfFontFamily(), "bold");
  doc.setFontSize(22);
  doc.text(opts.title, PDF_MARGIN, 72);
  doc.setFont(getPdfFontFamily(), "normal");
  doc.setFontSize(12);
  doc.setTextColor(80);
  doc.text(opts.subtitle, PDF_MARGIN, 82);
  let y = 90;
  if (opts.viewLabel) {
    doc.text(opts.viewLabel, PDF_MARGIN, y);
    y += 8;
  }
  doc.text(opts.generatedLabel, PDF_MARGIN, y);

  doc.setDrawColor(47, 126, 120);
  doc.setLineWidth(0.6);
  doc.line(PDF_MARGIN, y + 10, w - PDF_MARGIN, y + 10);

  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(opts.disclaimer, PDF_MARGIN, h - 20);

  doc.addPage();
  return PDF_MARGIN + PDF_HEADER_H + 4;
}
